import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ADMIN_PATH } from "../lib/adminPath";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "get_reta_admin_token";
// Per-tab cache of the password the admin most recently logged in with.
// Lives in sessionStorage (cleared on tab close, on `logout()`, and
// rotated after a successful self-service password change) so the
// "Change password" dialog can pre-fill the current-password field for
// admins working in a secured location. NEVER use this for anything
// other than that pre-fill — there's no auth signal here, it's
// strictly a typing-assist convenience.
const LAST_PW_KEY = "get_reta_admin_last_pw";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(token));

  const authAxios = useMemo(() => {
    // axios is dynamically imported so the admin-only HTTP client stays
    // out of the storefront's critical main bundle. The returned object
    // is promise-for-promise compatible with the axios instance the
    // admin pages have always used (get/post/put/patch/delete/request).
    let instPromise = null;
    const getInst = () => {
      if (!instPromise) {
        instPromise = import("axios").then(({ default: axios }) => {
          const inst = axios.create({ baseURL: API });
          inst.interceptors.request.use((cfg) => {
            const t = localStorage.getItem(TOKEN_KEY);
            if (t) cfg.headers.Authorization = `Bearer ${t}`;
            if (ADMIN_PATH) cfg.headers["X-Admin-Path"] = ADMIN_PATH;
            return cfg;
          });
          return inst;
        });
      }
      return instPromise;
    };
    const call =
      (method) =>
      (...args) =>
        getInst().then((inst) => inst[method](...args));
    return {
      get: call("get"),
      post: call("post"),
      put: call("put"),
      patch: call("patch"),
      delete: call("delete"),
      request: call("request"),
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setUser(null);
      return;
    }
    let cancelled = false;
    authAxios
      .get("/auth/me")
      .then((res) => {
        if (!cancelled) setUser(res.data);
      })
      .catch(() => {
        if (!cancelled) {
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setUser(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, authAxios]);

  const login = useCallback(
    async (email, password) => {
      const headers = ADMIN_PATH ? { "X-Admin-Path": ADMIN_PATH } : {};
      const { default: axios } = await import("axios");
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { headers }
      );
      // Successful password step — stash the plaintext for the
      // change-password pre-fill. Cleared on logout / replaced when
      // the password rotates. Kept in sessionStorage so it dies with
      // the tab.
      try {
        sessionStorage.setItem(LAST_PW_KEY, password);
      } catch {
        /* storage disabled — harmless, dialog just shows empty */
      }
      // If the backend asks for the second factor, return the challenge
      // payload to the caller without persisting anything.
      if (data && data.stage) {
        return { stage: data.stage, challenge: data };
      }
      // Legacy / no-2FA path (kept for safety; backend currently always 2FA).
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      return { stage: "done", user: data.user };
    },
    []
  );

  const verifyTotp = useCallback(async (challengeToken, code) => {
    const headers = ADMIN_PATH ? { "X-Admin-Path": ADMIN_PATH } : {};
    const { default: axios } = await import("axios");
    const { data } = await axios.post(
      `${API}/auth/totp/verify`,
      { challenge_token: challengeToken, code },
      { headers }
    );
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data; // caller gets user + optional recovery_codes / remaining
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    try {
      sessionStorage.removeItem(LAST_PW_KEY);
    } catch {
      /* ignore */
    }
    setToken(null);
    setUser(null);
  }, []);

  // Exposed for the change-password dialog so it can pre-fill the
  // current-password field. Returns "" if nothing has been stashed yet
  // (e.g. the admin reloaded the page — sessionStorage survives reload
  // but a fresh login is required to populate it the first time).
  const getLastPassword = useCallback(() => {
    try {
      return sessionStorage.getItem(LAST_PW_KEY) || "";
    } catch {
      return "";
    }
  }, []);

  // Called by the change-password dialog after a successful rotation
  // so the next time the dialog opens it pre-fills the new password
  // (not the now-invalid old one).
  const setLastPassword = useCallback((pw) => {
    try {
      if (pw) sessionStorage.setItem(LAST_PW_KEY, pw);
      else sessionStorage.removeItem(LAST_PW_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <AdminAuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        verifyTotp,
        logout,
        authAxios,
        getLastPassword,
        setLastPassword,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
